import { LitElement, html, nothing, svg } from "lit";

import { customElement, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { DataQueryController } from "ui-common";
import { DragDropController } from "ui-common/drag-drop-controller";
import { FileUploadError, formatUploadError, uploadFile } from "ui-common/services/file-upload";
import "ui-components/base-image-cropper/base-image-cropper";
import "ui-components/modal/wow-modal";
import { getAssetUrl } from "helpers/assetHelpers";

import { styles } from "./avatar-editor.css";

import { profileDataQueryOptions } from "../../queries/profile-data";
import { t, translate } from "../../translations/i18n";

@customElement("wow-avatar-editor")
export class AvatarEditor extends LitElement {
  static override styles = styles;

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  @state()
  private previewUrl?: string;

  private dragDropController = new DragDropController(
    this,
    (file) => this.handleDroppedFile(file),
    ["image/"], // Avatar should only accept images
  );

  protected override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    super.willUpdate(changedProperties);

    // Reset image error when currentAvatarUrl changes
    if (changedProperties.has("currentAvatarUrl")) {
      this.imageError = false;
    }
  }

  @state() private isCropperModalOpen = false;

  @state() private isEditModalOpen = false;

  @state()
  private uploading = false;

  @state()
  private selectedFile?: File;

  @state()
  private imageError = false;

  @state()
  private selectedFileUrl?: string;

  private handleFileSelect = () => {
    if (this.uploading) return;

    const fileInput = this.shadowRoot?.querySelector("#file-input") as HTMLInputElement;
    fileInput?.click();
  };

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.openEditModal();
    }
  };

  private handleEditAvatar() {
    this.isCropperModalOpen = true;

    // Prefer locally stored image (selectedFileUrl)
    const src = this.selectedFileUrl || this.previewUrl || this.profileDataQuery.result.data?.profileImageUrl || "";

    if (src.startsWith("blob:")) {
      this.selectedFileUrl = src;
    } else {
      // Convert backend relative/absolute URL
      this.selectedFileUrl = getAssetUrl(src);
    }
  }

  private handleFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    this.selectedFile = file;
    this.openCropperModal(file);

    // Reset input value
    input.value = "";
  };

  private openCropperModal(file: File) {
    this.isCropperModalOpen = true;
    this.selectedFileUrl = URL.createObjectURL(file);
  }

  private handleCropperModalClose = () => {
    this.isCropperModalOpen = false;
    this.selectedFile = undefined;
  };

  private handleBaseCropperSave = async (event: CustomEvent) => {
    if (this.uploading) return;
    try {
      this.uploading = true;
      const croppedFile = event.detail?.file;
      if (!croppedFile) {
        throw new Error("Failed to generate cropped file");
      }
      const result = await uploadFile(croppedFile, "profile_image");
      this.previewUrl = result.url;
      this.dispatchEvent(
        new CustomEvent("avatar-uploaded", {
          detail: { fileId: result.fileId, url: result.url },
          bubbles: true,
          composed: true,
        }),
      );
      this.handleCropperModalClose();
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      if (error instanceof FileUploadError) {
        console.error("Upload error:", formatUploadError(error));
      }
    } finally {
      this.uploading = false;
    }
  };

  private openEditModal() {
    if (this.uploading) return;

    const profileImageUrl = this.profileDataQuery.result.data?.profileImageUrl;
    const hasImage = Boolean(profileImageUrl) || Boolean(this.previewUrl);

    if (hasImage) {
      this.isEditModalOpen = true;
    } else {
      this.handleFileSelect();
    }
  }

  private handleEditClose() {
    this.isEditModalOpen = false;
  }

  private handleDroppedFile(file: File) {
    if (!file) return;

    this.selectedFile = file;
    this.openCropperModal(file);
  }

  // Removed: handleCrop (replaced by base-image-cropper)

  private renderFallbackAvatar() {
    return svg`
      <svg width="64" height="64" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="50" fill="#e5e7eb"/>
        <circle cx="50" cy="35" r="15" fill="#9ca3af"/>
        <ellipse cx="50" cy="75" rx="25" ry="15" fill="#9ca3af"/>
      </svg>
    `;
  }

  private renderAvatar() {
    const avatarUrl = this.previewUrl || this.profileDataQuery.result.data?.profileImageUrl;

    if (!avatarUrl) {
      return html` <div class="fallback-avatar">${this.renderFallbackAvatar()}</div> `;
    }

    const src = getAssetUrl(avatarUrl);
    if (!src) {
      return html` <div class="fallback-avatar">${this.renderFallbackAvatar()}</div> `;
    }

    return html` <img src=${src} alt="User avatar" class="avatar-image" /> `;
  }

  private renderEditModal() {
    if (!this.isEditModalOpen) return nothing;

    const avatarUrl = (this.previewUrl || this.profileDataQuery.result.data?.profileImageUrl) ?? "";
    const src = getAssetUrl(avatarUrl);

    return html`
      <wow-modal
        mobile-fullscreen
        variant="responsive"
        @close=${this.handleEditClose}
        @footer-button-clicked=${this.handleEditClose}
      >
        <span slot="header">${t("editAvatar.title")}</span>
        <span slot="footer-button-text">${t("editAvatar.close")}</span>

        <div class="edit-modal-content">
          ${src
            ? html`<img src=${src} alt="User avatar" class="avatar-image edit-modal" />`
            : html`<div class="fallback-avatar edit-modal">${this.renderFallbackAvatar()}</div>`}
          <div class="buttons-wrapper">
            <wow-button size="small" @click=${this.handleEditAvatar}>
              <ph-pencil-simple size="20"></ph-pencil-simple>
              ${t("editAvatar.edit")}
            </wow-button>
            <wow-button size="small" @click=${this.handleFileSelect}>
              <ph-camera size="20"></ph-camera>
              ${t("editAvatar.addNew")}
            </wow-button>
            <!-- <wow-button class="delete" variant="neutral" size="small">
              <ph-trash size="20"></ph-trash>
            </wow-button> -->
          </div>
        </div>
      </wow-modal>
    `;
  }

  private renderCropperModal() {
    if (!this.isCropperModalOpen || !this.selectedFileUrl) return nothing;
    return html`
      <base-image-cropper
        .open=${true}
        .imageUrl=${this.selectedFileUrl}
        .viewport=${"circle"}
        .outputWidth=${200}
        .outputHeight=${200}
        .headerText=${t("editAvatar.title")}
        .footerButtonText=${this.uploading ? translate("fileUpload.uploading") : translate("profile.save")}
        @cropper-save=${this.handleBaseCropperSave}
        @cropper-cancel=${this.handleCropperModalClose}
      ></base-image-cropper>
    `;
  }

  protected override render() {
    const containerClasses = {
      "avatar-container": true,
      uploading: this.uploading,
      dragging: this.dragDropController.isDragging,
    };

    return html`
      <!-- Hidden file input -->
      <input id="file-input" type="file" accept="image/*" class="file-input" @change=${this.handleFileSelected} />

      <!-- Avatar display -->
      <div
        class=${classMap(containerClasses)}
        @click=${this.openEditModal}
        @keydown=${this.handleKeydown}
        @dragover=${this.dragDropController.handleDragOver}
        @dragleave=${this.dragDropController.handleDragLeave}
        @drop=${this.dragDropController.handleDrop}
        role="button"
        tabindex="0"
        aria-label="Change avatar"
      >
        ${this.renderAvatar()}

        <div class="upload-overlay">
          ${this.uploading
            ? html`<ph-spinner class="spinner upload-icon" size="20"></ph-spinner>`
            : html`<ph-camera class="upload-icon" size="20"></ph-camera>`}
        </div>
      </div>

      ${this.renderEditModal()} ${this.renderCropperModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-avatar-editor": AvatarEditor;
  }
}
